import type { FC, ReactNode } from 'react'

import { useRef, useState } from 'react'
import Link from 'next/link'

import {
    Coffee,
    Info,
    Moon,
    Plus,
    TriangleAlert,
    X
} from 'lucide-react'

import { cn } from '@/lib/utils'

// Song et al. (2024, Int J Mol Sci): "the half-life is within 3 to 5 h". The
// estimate is shown as a range across that span rather than a single number,
// because FDA notes wide variation in how fast people eliminate caffeine.
const HALF_LIFE_FAST = 3
const HALF_LIFE_MID = 4
const HALF_LIFE_SLOW = 5

// CDC/NIOSH: "Avoid caffeine ... for 5 or more hours before sleep". Drake et al.
// (2013) still measured lost sleep with a 400 mg dose taken 6 hours before bed.
const CDC_HOURS = 5
const MARGIN_HOURS = 6

const MAX_DRINKS = 8

interface Preset {
    key: string
    label: string
    detail: string
    mg: number
}

// FDA "Spilling the Beans" figures, per 12 fl oz unless stated. Where FDA gives a
// range, the default is its midpoint and the range is shown so the visitor can
// adjust it to their own drink's label.
const PRESETS: Preset[] = [
    { key: 'coffee', label: 'Brewed coffee, 12 fl oz', detail: 'FDA range 113 to 247 mg', mg: 180 },
    { key: 'black-tea', label: 'Black tea, 12 fl oz', detail: 'FDA figure 71 mg', mg: 71 },
    { key: 'green-tea', label: 'Green tea, 12 fl oz', detail: 'FDA figure 37 mg', mg: 37 },
    { key: 'soft-drink', label: 'Caffeinated soft drink, 12 fl oz', detail: 'FDA range 23 to 83 mg', mg: 53 },
    { key: 'energy-drink', label: 'Energy drink, 12 fl oz', detail: 'FDA range 41 to 246 mg, check the can', mg: 144 },
    { key: 'decaf', label: 'Decaf coffee, 8 fl oz', detail: 'FDA range 2 to 15 mg', mg: 9 },
    { key: 'custom', label: 'Something else', detail: 'Enter the milligrams from the label', mg: 100 }
]

interface Drink {
    id: number
    preset: string
    time: string
    mg: number
}

const INITIAL_DRINKS: Drink[] = [
    { id: 1, preset: 'coffee', time: '08:00', mg: 180 },
    { id: 2, preset: 'black-tea', time: '15:30', mg: 71 }
]

const parseTime = (value: string): number | null => {
    const match = /^(\d{1,2}):(\d{2})$/.exec(value)
    if (!match) return null

    const hours = Number(match[1])
    const minutes = Number(match[2])
    if (hours > 23 || minutes > 59) return null

    return hours * 60 + minutes
}

const formatClock = (total: number): string => {
    const wrapped = ((total % 1440) + 1440) % 1440
    const hours24 = Math.floor(wrapped / 60)
    const minutes = wrapped % 60
    const suffix = hours24 < 12 ? 'am' : 'pm'
    const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12

    return `${hours12}:${String(minutes).padStart(2, '0')} ${suffix}`
}

const formatGap = (total: number): string => {
    const hours = Math.floor(total / 60)
    const minutes = total % 60
    if (hours === 0) return `${minutes}m`

    return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`
}

// Every drink is counted back from bedtime within the 24 hours before it, so a
// bedtime after midnight still pairs correctly with drinks from the evening.
const minutesBeforeBed = (bed: number, drink: number): number => (bed - drink + 1440) % 1440

const remainingAt = (drinks: Drink[], bed: number, halfLife: number): number =>
    drinks.reduce((sum, drink) => {
        const time = parseTime(drink.time)
        if (time === null || !Number.isFinite(drink.mg) || drink.mg <= 0) return sum

        const hours = minutesBeforeBed(bed, time) / 60
        return sum + drink.mg * Math.pow(0.5, hours / halfLife)
    }, 0)

const formatMg = (value: number): string => (value < 1 ? 'under 1' : String(Math.round(value)))

// Plain-language yardstick using the same FDA drink figures as the presets.
const compare = (mg: number): string => {
    if (mg < 15) return 'no more than a cup of decaf coffee'
    if (mg < 37) return 'less than a 12 fl oz green tea'
    if (mg < 71) return 'between a 12 fl oz green tea and a black tea'
    if (mg < 113) return 'more than a 12 fl oz black tea'
    return 'as much as a whole cup of brewed coffee'
}

export const CaffeineCalculator: FC = (): ReactNode => {
    const [bedTime, setBedTime] = useState('22:30')
    const [drinks, setDrinks] = useState<Drink[]>(INITIAL_DRINKS)
    const nextId = useRef(INITIAL_DRINKS.length + 1)

    const bed = parseTime(bedTime)

    const update = (id: number, patch: Partial<Drink>) => {
        setDrinks((previous) => previous.map((drink) => (drink.id === id ? { ...drink, ...patch } : drink)))
    }

    const choosePreset = (id: number, key: string) => {
        const preset = PRESETS.find((item) => item.key === key)
        update(id, { preset: key, mg: preset ? preset.mg : 100 })
    }

    const addDrink = () => {
        if (drinks.length >= MAX_DRINKS) return
        const id = nextId.current
        nextId.current += 1
        setDrinks((previous) => [...previous, { id, preset: 'coffee', time: '12:00', mg: 180 }])
    }

    const removeDrink = (id: number) => {
        setDrinks((previous) => previous.filter((drink) => drink.id !== id))
    }

    const low = bed === null ? 0 : remainingAt(drinks, bed, HALF_LIFE_FAST)
    const mid = bed === null ? 0 : remainingAt(drinks, bed, HALF_LIFE_MID)
    const high = bed === null ? 0 : remainingAt(drinks, bed, HALF_LIFE_SLOW)
    const hasDrinks = drinks.some((drink) => parseTime(drink.time) !== null && Number.isFinite(drink.mg) && drink.mg > 0)

    return (
        <section
            aria-label='Caffeine calculator'
            className='my-10 overflow-hidden rounded-2xl border border-border bg-card shadow-sm'
        >
            <div className='border-b border-border bg-secondary/50 px-5 py-4 sm:px-6'>
                <div className='flex items-center gap-2'>
                    <Coffee className='size-5 shrink-0 text-primary' />
                    <span className='font-heading text-lg font-semibold tracking-tight text-foreground'>
                        Caffeine calculator
                    </span>
                </div>
                <span className='mt-1 block text-sm text-muted-foreground'>
                    Add today&apos;s drinks to see roughly how much caffeine is left when you go to bed.
                </span>
            </div>

            <div className='px-5 py-5 sm:px-6'>
                <div>
                    <label htmlFor='caffeine-calculator-bedtime' className='block text-sm font-medium text-foreground'>
                        Bedtime
                    </label>
                    <input
                        id='caffeine-calculator-bedtime'
                        type='time'
                        value={bedTime}
                        onChange={(event) => setBedTime(event.target.value)}
                        className='mt-2 w-full rounded-xl border border-border bg-background px-4 py-3 text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring sm:w-56'
                    />
                </div>

                <span className='mt-6 block text-sm font-medium text-foreground'>Your drinks today</span>

                <div className='mt-3 flex flex-col gap-3'>
                    {drinks.map((drink, index) => {
                        const time = parseTime(drink.time)
                        const gap = bed !== null && time !== null ? minutesBeforeBed(bed, time) : null
                        const late = gap !== null && gap < MARGIN_HOURS * 60
                        const preset = PRESETS.find((item) => item.key === drink.preset)

                        return (
                            <div
                                key={drink.id}
                                className={cn(
                                    'rounded-xl border px-4 py-3',
                                    late ? 'border-primary/50 bg-primary/5' : 'border-border bg-background'
                                )}
                            >
                                <div className='grid grid-cols-1 gap-3 sm:grid-cols-[1fr_7.5rem_6.5rem_auto] sm:items-end'>
                                    <div>
                                        <label htmlFor={`caffeine-drink-${drink.id}`} className='block text-xs font-medium text-muted-foreground'>
                                            Drink {index + 1}
                                        </label>
                                        <select
                                            id={`caffeine-drink-${drink.id}`}
                                            value={drink.preset}
                                            onChange={(event) => choosePreset(drink.id, event.target.value)}
                                            className='mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring'
                                        >
                                            {PRESETS.map((item) => (
                                                <option key={item.key} value={item.key}>
                                                    {item.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label htmlFor={`caffeine-time-${drink.id}`} className='block text-xs font-medium text-muted-foreground'>
                                            Time
                                        </label>
                                        <input
                                            id={`caffeine-time-${drink.id}`}
                                            type='time'
                                            value={drink.time}
                                            onChange={(event) => update(drink.id, { time: event.target.value })}
                                            className='mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring'
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor={`caffeine-mg-${drink.id}`} className='block text-xs font-medium text-muted-foreground'>
                                            Caffeine (mg)
                                        </label>
                                        <input
                                            id={`caffeine-mg-${drink.id}`}
                                            type='number'
                                            inputMode='numeric'
                                            min={0}
                                            max={1000}
                                            value={Number.isFinite(drink.mg) ? drink.mg : ''}
                                            onChange={(event) => {
                                                const raw = event.target.value
                                                update(drink.id, {
                                                    mg: raw === '' ? Number.NaN : Math.min(1000, Math.max(0, Number(raw)))
                                                })
                                            }}
                                            className='mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring'
                                        />
                                    </div>

                                    <button
                                        type='button'
                                        onClick={() => removeDrink(drink.id)}
                                        aria-label={`Remove drink ${index + 1}`}
                                        className='inline-flex h-10 items-center justify-center gap-1 rounded-lg border border-border px-3 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                                    >
                                        <X className='size-4' />
                                        <span className='sm:sr-only'>Remove</span>
                                    </button>
                                </div>

                                <span className='mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground'>
                                    {preset && <span>{preset.detail}</span>}
                                    {gap !== null && <span>{formatGap(gap)} before bed</span>}
                                    {late && (
                                        <span className='inline-flex items-center gap-1 font-medium text-foreground'>
                                            <TriangleAlert className='size-3.5 text-primary' />
                                            Within {MARGIN_HOURS} hours of bed
                                        </span>
                                    )}
                                </span>
                            </div>
                        )
                    })}
                </div>

                <button
                    type='button'
                    onClick={addDrink}
                    disabled={drinks.length >= MAX_DRINKS}
                    className='mt-3 inline-flex items-center gap-2 rounded-full border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50'
                >
                    <Plus className='size-4' />
                    {drinks.length >= MAX_DRINKS ? `Up to ${MAX_DRINKS} drinks` : 'Add a drink'}
                </button>

                <div aria-live='polite' className='mt-6 rounded-xl border border-primary/40 bg-primary/5 px-4 py-4'>
                    {bed === null ? (
                        <span className='block text-sm text-muted-foreground'>Enter a bedtime to see your estimate.</span>
                    ) : !hasDrinks ? (
                        <span className='block text-sm text-muted-foreground'>
                            Add a drink to see how much caffeine is left at bedtime.
                        </span>
                    ) : (
                        <div>
                            <span className='block text-sm font-medium text-foreground'>
                                Estimated caffeine left at {formatClock(bed)}
                            </span>
                            <span className='mt-1 block font-heading text-3xl font-semibold tracking-tight text-foreground'>
                                {mid < 1 ? 'Less than 1 mg' : `About ${Math.round(mid)} mg`}
                            </span>
                            <span className='mt-1 block text-sm leading-relaxed text-muted-foreground'>
                                Somewhere between {formatMg(low)} and {formatMg(high)} mg, depending on how
                                quickly your body clears it. At the upper end, that is {compare(high)}.
                            </span>
                        </div>
                    )}
                </div>

                {bed !== null && (
                    <div className='mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2'>
                        <div className='rounded-xl border border-border bg-background px-4 py-3'>
                            <span className='flex items-center gap-2 text-xs font-medium text-muted-foreground'>
                                <Moon className='size-4 shrink-0 text-primary' />
                                CDC advice: {CDC_HOURS} or more hours before sleep
                            </span>
                            <span className='mt-1 block font-heading text-2xl font-semibold tracking-tight text-foreground'>
                                Last caffeine by {formatClock(bed - CDC_HOURS * 60)}
                            </span>
                        </div>
                        <div className='rounded-xl border border-border bg-background px-4 py-3'>
                            <span className='flex items-center gap-2 text-xs font-medium text-muted-foreground'>
                                <Moon className='size-4 shrink-0 text-primary' />
                                With more margin: {MARGIN_HOURS} hours
                            </span>
                            <span className='mt-1 block font-heading text-2xl font-semibold tracking-tight text-foreground'>
                                Last caffeine by {formatClock(bed - MARGIN_HOURS * 60)}
                            </span>
                        </div>
                    </div>
                )}

                <div className='mt-5 flex gap-2 rounded-xl border border-border bg-secondary/40 px-4 py-3'>
                    <Info className='mt-0.5 size-4 shrink-0 text-muted-foreground' />
                    <span className='text-xs leading-relaxed text-muted-foreground'>
                        A rough estimate, not a measurement. It assumes caffeine halves every 3 to 5
                        hours, the range reported for adults, and drink strength varies a lot. Nothing
                        you enter is stored. See{' '}
                        <Link href='/sleep/caffeine-and-sleep' className='font-normal text-inherit underline underline-offset-2 hover:text-foreground'>
                            how long caffeine affects sleep
                        </Link>
                        .
                    </span>
                </div>
            </div>
        </section>
    )
}

export default CaffeineCalculator
